import Papa from 'papaparse';
import { saveAs } from 'file-saver';
import { supabase } from '../supabase';
import type {
  EmailCampaign,
  EmailCampaignPreparata,
  EmailCampaignPubblico,
  EmailCampaignStatus,
  EmailConsent,
  EmailConsentAction,
  EmailContact,
  EmailContactConListe,
  EmailContactLink,
  EmailContactStatus,
  EmailImportBasis,
  EmailImportResult,
  EmailImportRow,
  EmailSaluteLista,
} from '../../types/email.types';
import { eventiService } from '../eventi/eventiService';
import type { PartecipazioneContatto } from '../eventi/tipi';
import { documentoVuoto } from '@email/index.ts';
import type { DocumentoEmail } from '@email/types.ts';

/**
 * Accesso ai dati del modulo email.
 *
 * Le tabelle email_* non esistono ancora in database.types.ts, che si rigenera
 * da Supabase con `supabase gen types`. Finche' non c'e', il client va forzato:
 * si fa qui una volta sola, con il perche' scritto, invece di ripetere un cast
 * a ogni query come succede in leadService.
 * Quando i tipi saranno rigenerati, questa riga sparisce e non cambia altro.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export interface ContactFilters {
  search?: string;
  status?: EmailContactStatus | 'tutti';
  /** Solo i contatti di questa lista. */
  listaId?: string | null;
  page?: number;
  pageSize?: number;
}

export const CONTACTS_PAGE_SIZE = 50;

export interface ContactsPage {
  contatti: EmailContactConListe[];
  totale: number;
}

/** Riga come arriva dal database, con le liste agganciate. */
type RigaContatto = EmailContact & {
  tutte_le_liste?: { tag_id: string }[];
  filtro_lista?: unknown;
};

export interface ContactStats {
  totale: number;
  attivi: number;
  inAttesa: number;
  fuori: number;
}

/** Dettaglio di un contatto: anagrafica, collegamenti, storico del consenso ed eventi. */
export interface ContactDetail {
  contatto: EmailContact;
  collegamenti: EmailContactLink[];
  consensi: EmailConsent[];
  /** Gli eventi a cui partecipa, con richiesta e assegnazione. */
  eventi?: PartecipazioneContatto[];
  /** Vero se gli eventi non si sono potuti leggere: la scheda lo dice invece di mostrare "nessun evento". */
  eventiNonLeggibili?: boolean;
}

/** Filtri comuni a elenco, selezione e esportazione. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applicaFiltri(query: any, filters: ContactFilters) {
  let q = query;
  if (filters.status && filters.status !== 'tutti') {
    q = q.eq('status', filters.status);
  }
  if (filters.listaId) {
    q = q.eq('filtro_lista.tag_id', filters.listaId);
  }
  const term = filters.search?.trim();
  if (term) {
    // Le virgole dentro il termine spezzerebbero la sintassi di `or`.
    const pulito = term.replace(/[,()]/g, ' ');
    q = q.or(`email.ilike.%${pulito}%,full_name.ilike.%${pulito}%,company_name.ilike.%${pulito}%`);
  }
  return q;
}

async function contaPerStato(stati: EmailContactStatus[]): Promise<number> {
  const { count, error } = await db
    .from('email_contacts')
    .select('id', { count: 'exact', head: true })
    .in('status', stati);
  if (error) throw error;
  return count ?? 0;
}

export const emailService = {
  async getContacts(filters: ContactFilters = {}): Promise<ContactsPage> {
    const page = filters.page ?? 0;
    const pageSize = filters.pageSize ?? CONTACTS_PAGE_SIZE;
    const da = page * pageSize;

    // Le liste si agganciano due volte: una per mostrarle tutte accanto al
    // contatto, una (solo se si filtra) per tenere i contatti di una lista.
    // Con un aggancio solo, filtrando, accanto al contatto resterebbe
    // soltanto la lista del filtro.
    const colonne = filters.listaId
      ? '*, tutte_le_liste:email_contact_tags(tag_id), filtro_lista:email_contact_tags!inner(tag_id)'
      : '*, tutte_le_liste:email_contact_tags(tag_id)';

    const query = applicaFiltri(
      db.from('email_contacts').select(colonne, { count: 'exact' }),
      filters
    )
      .order('created_at', { ascending: false })
      .range(da, da + pageSize - 1);

    const { data, error, count } = await query;
    if (error) throw error;

    return {
      contatti: ((data || []) as RigaContatto[]).map((riga) => {
        // Il filtro serviva solo alla query: non fa parte del contatto.
        const { tutte_le_liste, ...resto } = riga;
        delete resto.filtro_lista;
        return { ...resto, liste: (tutte_le_liste ?? []).map((t) => t.tag_id) };
      }),
      totale: count ?? 0,
    };
  },

  /**
   * Tutti gli identificativi che corrispondono ai filtri, non solo la pagina
   * visibile. Serve a "seleziona tutti" prima di metterli in una lista.
   */
  async idsCorrispondenti(filters: ContactFilters = {}): Promise<string[]> {
    // Il server non manda piu' di mille righe per richiesta e non avvisa del
    // taglio: si legge a blocchi, in ordine fisso, finche' ne arrivano.
    const BLOCCO = 1000;
    const TETTO = 20000;
    const colonne = filters.listaId ? 'id, filtro_lista:email_contact_tags!inner(tag_id)' : 'id';
    const ids: string[] = [];
    while (ids.length < TETTO) {
      const { data, error } = await applicaFiltri(db.from('email_contacts').select(colonne), filters)
        .order('id')
        .range(ids.length, ids.length + BLOCCO - 1);
      if (error) throw error;
      const righe = (data || []) as { id: string }[];
      if (righe.length === 0) return ids;
      ids.push(...righe.map((r) => r.id));
    }
    throw new Error(`Sono piu' di ${TETTO} contatti: restringi la ricerca e selezionali a gruppi.`);
  },

  async getStats(): Promise<ContactStats> {
    const [totale, attivi, inAttesa, fuori] = await Promise.all([
      contaPerStato(['in_attesa', 'attivo', 'disiscritto', 'bounce', 'reclamo', 'soppresso']),
      contaPerStato(['attivo']),
      contaPerStato(['in_attesa']),
      contaPerStato(['disiscritto', 'bounce', 'reclamo', 'soppresso']),
    ]);
    return { totale, attivi, inAttesa, fuori };
  },

  async getContactDetail(id: string): Promise<ContactDetail> {
    const [contatto, collegamenti, consensi, eventi] = await Promise.all([
      db.from('email_contacts').select('*').eq('id', id).single(),
      db.from('email_contact_links').select('*').eq('contact_id', id),
      db
        .from('email_consents')
        .select('*')
        .eq('contact_id', id)
        .order('occurred_at', { ascending: false }),
      // Gli eventi sono un di piu': se non si leggono (ruolo senza accesso,
      // tabelle non ancora create) la scheda si apre lo stesso e lo dice.
      eventiService.partecipazioniDelContatto(id).then(
        (righe) => ({ righe, errore: false }),
        () => ({ righe: [] as PartecipazioneContatto[], errore: true })
      ),
    ]);

    if (contatto.error) throw contatto.error;
    if (collegamenti.error) throw collegamenti.error;
    if (consensi.error) throw consensi.error;

    return {
      contatto: contatto.data as unknown as EmailContact,
      collegamenti: (collegamenti.data || []) as unknown as EmailContactLink[],
      consensi: (consensi.data || []) as unknown as EmailConsent[],
      eventi: eventi.righe,
      eventiNonLeggibili: eventi.errore,
    };
  },

  /**
   * Aggiunge un contatto a mano. La base giuridica e' obbligatoria come
   * nell'importazione: anche un contatto solo deve lasciare una prova.
   */
  async addContact(
    riga: EmailImportRow,
    fonte: string,
    base: EmailImportBasis,
    note?: string
  ): Promise<EmailImportResult> {
    return emailService.importContacts([riga], fonte, base, new Date(), note);
  },

  async importContacts(
    righe: EmailImportRow[],
    fonte: string,
    base: EmailImportBasis,
    dataConsenso: Date,
    note?: string
  ): Promise<EmailImportResult> {
    const { data, error } = await db.rpc('email_import_contatti', {
      p_righe: righe,
      p_fonte: fonte,
      p_base_giuridica: base,
      p_data_consenso: dataConsenso.toISOString(),
      p_note: note ?? null,
    });
    if (error) throw error;
    return data as unknown as EmailImportResult;
  },

  /**
   * Registra una revoca o un altro evento di consenso. Non tocca mai lo stato
   * del contatto direttamente: quello lo ricalcola il database.
   */
  async registraConsenso(
    contactId: string,
    action: EmailConsentAction,
    source: string,
    note?: string
  ): Promise<void> {
    const { error } = await db.from('email_consents').insert({
      contact_id: contactId,
      action,
      source,
      source_detail: note ? { note } : null,
    });
    if (error) throw error;
  },

  /** Disiscrive a mano, su richiesta arrivata per telefono o di persona. */
  async disiscrivi(contactId: string, motivo: string): Promise<void> {
    await emailService.registraConsenso(contactId, 'opt_out', motivo);
  },

  async esportaCsv(filters: ContactFilters = {}): Promise<number> {
    // L'export non e' paginato: serve tutto, ma con un tetto difensivo.
    const TETTO = 10000;
    const { contatti } = await emailService.getContacts({
      ...filters,
      page: 0,
      pageSize: TETTO,
    });

    const righe = contatti.map((c) => ({
      email: c.email,
      nome: c.full_name ?? '',
      azienda: c.company_name ?? '',
      telefono: c.phone ?? '',
      citta: c.city ?? '',
      provincia: c.province ?? '',
      stato: c.status,
      base_consenso: c.consent_basis ?? '',
      data_consenso: c.consent_at ?? '',
      origine: c.source ?? '',
    }));

    const csv = Papa.unparse(righe, { delimiter: ';' });
    const oggi = new Date().toISOString().slice(0, 10);
    // Il BOM serve a Excel per aprire il file in UTF-8 senza sfasare gli accenti.
    saveAs(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }), `contatti-email-${oggi}.csv`);
    return righe.length;
  },
};

// ---------------------------------------------------------------------------
// Campagne
// ---------------------------------------------------------------------------

export const campaignService = {
  async elenco(): Promise<EmailCampaign[]> {
    const { data, error } = await db
      .from('email_campaigns')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []) as EmailCampaign[];
  },

  async dettaglio(id: string): Promise<EmailCampaign> {
    const { data, error } = await db.from('email_campaigns').select('*').eq('id', id).single();
    if (error) throw error;
    return data as EmailCampaign;
  },

  async crea(nome: string): Promise<EmailCampaign> {
    const { data, error } = await db
      .from('email_campaigns')
      .insert({ name: nome, subject: '', document: documentoVuoto() })
      .select('*')
      .single();
    if (error) throw error;
    return data as EmailCampaign;
  },

  async salva(
    id: string,
    campi: Partial<{
      name: string;
      subject: string;
      preheader: string | null;
      document: DocumentoEmail;
      from_email: string | null;
      from_name: string | null;
      reply_to: string | null;
      status: EmailCampaignStatus;
      scheduled_at: string | null;
      pubblico: EmailCampaignPubblico;
      tag_filter: string[] | null;
    }>
  ): Promise<void> {
    const { error } = await db.from('email_campaigns').update(campi).eq('id', id);
    if (error) throw error;
  },

  /**
   * Ricalcola chi ricevera' la campagna. Lo decide il database: qui si
   * mostra solo il risultato, compreso quanti restano fuori per mancanza di
   * consenso - un numero che va visto prima di premere invia, non dopo.
   */
  async preparaDestinatari(id: string): Promise<EmailCampaignPreparata> {
    const { data, error } = await db.rpc('email_campagna_prepara', { p_campaign_id: id });
    if (error) throw error;
    return data as EmailCampaignPreparata;
  },

  /**
   * Salva a chi va la campagna. Il database cancella da solo i destinatari
   * calcolati prima: vanno ricalcolati.
   */
  async salvaPubblico(id: string, pubblico: EmailCampaignPubblico, liste: string[]): Promise<void> {
    await campaignService.salva(id, {
      pubblico,
      tag_filter: pubblico === 'liste' ? liste : null,
    });
  },

  async programma(id: string, quando: Date): Promise<void> {
    await campaignService.salva(id, {
      status: 'programmata',
      scheduled_at: quando.toISOString(),
    });
  },

  async sospendi(id: string): Promise<void> {
    await campaignService.salva(id, { status: 'sospesa' });
  },

  async riprendi(id: string): Promise<void> {
    await campaignService.salva(id, { status: 'in_invio' });
  },

  async annulla(id: string): Promise<void> {
    await campaignService.salva(id, { status: 'annullata' });
  },

  async elimina(id: string): Promise<void> {
    const { error } = await db.from('email_campaigns').delete().eq('id', id);
    if (error) throw error;
  },

  /** Quante email si possono ancora spedire nelle prossime 24 ore. */
  async quotaResidua(): Promise<number> {
    const { data, error } = await db.rpc('email_quota_giornaliera_residua');
    if (error) throw error;
    return (data as number) ?? 0;
  },

  async saluteLista(): Promise<EmailSaluteLista | null> {
    const { data, error } = await db.from('email_salute_lista').select('*').maybeSingle();
    if (error) throw error;
    return (data as EmailSaluteLista) ?? null;
  },

  /** In quale modalita' sta il motore: prova o invio reale. */
  async modalita(): Promise<'sandbox' | 'reale'> {
    const { data, error } = await db
      .from('email_settings')
      .select('value')
      .eq('key', 'modalita')
      .maybeSingle();
    if (error) throw error;
    return ((data?.value as string) ?? 'sandbox') as 'sandbox' | 'reale';
  },
};
